import { useEffect, useMemo, useRef, useState } from "react";
import { Camera, ImageIcon, ImagePlus, X } from "lucide-react";
import { cn } from "@/lib/utils";

const EXT = ["jpg", "jpeg", "png", "heic", "heif"];
const TYPE_EXT = { "image/jpeg": "jpg", "image/png": "png", "image/heic": "heic", "image/heif": "heif" };
const ACCEPT = "image/jpeg,image/png,image/heic,image/heif,.jpg,.jpeg,.png,.heic,.heif";
const MAX_FILES = 10;
const MAX_SIZE = 10 * 1024 * 1024;

const extOf = (f) => (f.name.includes(".") ? f.name.split(".").pop().toLowerCase() : "");

const normalize = (f) =>
  EXT.includes(extOf(f)) ? f : new File([f], `foto-${Date.now()}.${TYPE_EXT[f.type]}`, { type: f.type });

export const PhotoDrop = ({ files, onChange, prefix }) => {
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const pickRef = useRef(null);
  const camRef = useRef(null);
  const previews = useMemo(() => files.map((f) => ({ file: f, url: URL.createObjectURL(f) })), [files]);
  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);

  const addFiles = (list) => {
    const errs = [];
    const valid = [];
    Array.from(list || []).forEach((f) => {
      if (!EXT.includes(extOf(f)) && !TYPE_EXT[f.type]) errs.push(`${f.name}: nur JPG, PNG oder HEIC erlaubt.`);
      else if (f.size > MAX_SIZE) errs.push(`${f.name} ist größer als 10 MB.`);
      else valid.push(normalize(f));
    });
    const merged = [...files, ...valid];
    if (merged.length > MAX_FILES) errs.push(`Maximal ${MAX_FILES} Fotos möglich.`);
    onChange(merged.slice(0, MAX_FILES));
    setError(errs.join(" "));
  };

  const onInput = (e) => {
    addFiles(e.target.files);
    e.target.value = "";
  };

  return (
    <div className="space-y-2.5">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
        data-testid={`${prefix}-photo-dropzone`}
        className={cn(
          "rounded-2xl border-2 border-dashed p-4 text-center transition-colors",
          dragging ? "border-brand-blue bg-brand-sky-light" : "border-slate-300 bg-brand-mist/70",
        )}
      >
        <ImagePlus className="mx-auto h-7 w-7 text-brand-blue" strokeWidth={1.75} />
        <p className="mt-1.5 text-sm font-semibold text-brand-anthracite">Fotos hierher ziehen</p>
        <p className="text-xs text-slate-500">bis zu 10 Fotos · je max. 10 MB · JPG, PNG, HEIC</p>
        <div className="mt-3 flex flex-wrap justify-center gap-2">
          <button type="button" onClick={() => pickRef.current?.click()} data-testid={`${prefix}-photo-select`}
            className="inline-flex items-center gap-1.5 rounded-full border border-brand-blue bg-white px-4 py-2 text-sm font-bold text-brand-blue transition-colors hover:bg-brand-sky-light">
            <ImageIcon className="h-4 w-4" /> Fotos auswählen
          </button>
          <button type="button" onClick={() => camRef.current?.click()} data-testid={`${prefix}-photo-camera`}
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-anthracite px-4 py-2 text-sm font-bold text-white lg:hidden">
            <Camera className="h-4 w-4" /> Kamera
          </button>
        </div>
        <input ref={pickRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={onInput} data-testid={`${prefix}-photo-input`} />
        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onInput} data-testid={`${prefix}-photo-camera-input`} />
      </div>
      {previews.length > 0 && (
        <ul className="grid grid-cols-4 gap-2 sm:grid-cols-5" data-testid={`${prefix}-photo-list`}>
          {previews.map((p, i) => (
            <li key={p.url} className="relative aspect-square overflow-hidden rounded-xl bg-brand-mist ring-1 ring-slate-200">
              <ImageIcon className="absolute inset-0 m-auto h-6 w-6 text-slate-400" />
              <img src={p.url} alt={`Schadensfoto ${i + 1}`} className="relative h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              <button type="button" aria-label={`Foto ${i + 1} entfernen`} onClick={() => onChange(files.filter((_, idx) => idx !== i))}
                data-testid={`${prefix}-photo-remove-${i}`}
                className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-brand-anthracite/85 text-white hover:bg-red-600">
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span data-testid={`${prefix}-photo-count`}>{files.length}/{MAX_FILES} Fotos</span>
      </div>
      {error && <p role="alert" className="text-xs font-semibold text-red-600" data-testid={`${prefix}-photo-error`}>{error}</p>}
    </div>
  );
};
